import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import "admin-lte/dist/css/adminlte.min.css";
import { useLocalization } from "../../../context/LocalizationContext";
import { Formik, Form, Field } from "formik";
import Select from "react-select";
import * as Yup from "yup";
import apiManager from "../../../api/apiManager";
import { getCurrencySymbol } from "../../../utils/currencySymbols";
import API_ENDPOINTS from "../../../api/apiEndpoints";
import {
  showLoader,
  hideLoader,
} from "../../../shared/Services/Loader/loaderService";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../shared/Services/toasterService";
import FormLabel from "../../../shared/helpers/FormLabel";
import { useNavigate } from "react-router-dom";
import { encryptPayload } from "../../../utils/EncryptionDecryption";

const IMAGE_FILE_SIZE = 1024 * 1024;
const VIDEO_FILE_SIZE = 25 * 1024 * 1024;
const SUPPORTED_FORMATS = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "video/mp4",
  "video/webm",
  "video/ogg",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-matroska",
];

const createValidationSchema = (t) =>
  Yup.object({
    opCode: Yup.string()
      .matches(/^[A-Za-z0-9]*$/, "OpCode must be alphanumeric")
      .test(
        "length-if-present",
        "OpCode must be between 3 and 10 characters",
        (value) => {
          if (!value) return true;
          return value.length >= 3 && value.length <= 10;
        },
      ),
    serviceName: Yup.string()
      .required(t("validation_service_name_required"))
      .max(50, t("validation_service_name_max"))
      .test(
        "not-blank",
        t("validation_service_name_not_blank"),
        (v) => v && v.trim().length > 0,
      )
      .test(
        "not-dash-zero",
        t("validation_service_name_not_dash_zero"),
        (v) => v && v.trim() !== "-00",
      )
      .test(
        "no-leading-trailing-spaces",
        t("validation_servicename_no_leading_trailing_spaces"),
        (v) => v === (v || "").trim(),
      )
      .test(
        "no-multi-spaces",
        t("validation_service_name_no_multiple_spaces"),
        (v) => !/\s{2,}/.test(v || ""), // fails if 2 or more spaces anywhere
      )
      .matches(
        /^[A-Za-z0-9\s\-/&]*$/,
        t("validation_service_name_allowed_chars"),
      ),
    price: Yup.number()
      .typeError(t("validation_price_invalid")) // handles non-numeric input
      .required(t("validation_price_required"))
      .positive(t("validation_price_two_decimals_positive"))
      .max(100000, t("validation_price_max_100000")) // âœ… max value check
      .test(
        "max-two-decimals",
        t("validation_price_two_decimals_positive"),
        (value) =>
          value === undefined || /^\d+(\.\d{1,2})?$/.test(value.toString()),
      ),

    serviceType: Yup.string().required(t("validation_service_type_required")),
    mileageInterval: Yup.number().when("isMileageBased", {
      is: true,
      then: (schema) =>
        schema
          .typeError(t("validation_mileage_interval_invalid"))
          .required(t("validation_mileage_interval_required"))
          .positive(t("validation_mileage_interval_positive_two_decimals"))
          .max(100000, t("validation_mileage_interval_max_100000"))
          .test(
            "max-two-decimals",
            t("validation_mileage_interval_positive_two_decimals"),
            (value) =>
              value === undefined || /^\d+(\.\d{1,2})?$/.test(value.toString()),
          ),
      otherwise: (schema) => schema.notRequired(),
    }),

    mileageIntervalUnit: Yup.string().when(
      ["isMileageBased", "IsMileageBased"],
      {
        is: true,
        then: (schema) =>
          schema.required(t("validation_mileage_interval_unit_required")),
        otherwise: (schema) => schema.notRequired(),
      },
    ),
    ServiceImagePath: Yup.mixed()
      .notRequired()
      .test("fileSize", t("validation_logo_file_size"), (value) => {
        if (typeof value === "string") return true;
        if (!value) return true;

        if (value.type?.startsWith("video/")) {
          return value.size <= VIDEO_FILE_SIZE;
        }

        return value.size <= IMAGE_FILE_SIZE;
      })
      .test("fileFormat", t("validation_logo_file_format"), (value) => {
        if (typeof value === "string") return true;
        return !value || (value && SUPPORTED_FORMATS.includes(value.type));
      }),
    isMileageBased: Yup.boolean(),
    isOptional: Yup.boolean(),
    tenantId: Yup.string().required(t("validation_tenant_required")),
  });

const DealerServices = () => {
  const { t } = useLocalization();
  const dealerServiceValidationSchema = createValidationSchema(t);
  const navigate = useNavigate();

  const [gridSearch, setGridSearch] = useState("");
  const serviceColumns = [
    {
      name: t("service_name"),
      selector: (row) => row.serviceName,
      sortable: true,
      wrap: true,
    },
    {
      name: "OpCode",
      selector: (row) => row.opCode || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: t("price"),
      selector: (row) => {
        const currencyCode = (
          row.currency ||
          row.currencyCode ||
          "USD"
        ).toUpperCase();
        const symbol = getCurrencySymbol(currencyCode);
        const amount = Number(row.price ?? 0);
        return `${symbol}${amount.toFixed(2)}`;
      },
      sortable: true,
      wrap: true,
    },
    {
      name: t("service_type"),
      selector: (row) => row.serviceType,
      sortable: true,
      wrap: true,
    },
    {
      name: (
        <div style={{ width: "100%", textAlign: "left" }}>{t("actions")}</div>
      ),
      cell: (row) => (
        <div className="d-flex justify-content-start align-items-center gap-2">
          <button
            className="btn btn-sm text-white btn-blue d-flex justify-content-center align-items-center"
            onClick={() => fetchServiceDetails(row.serviceId)}
            title={t("view_details")}
          >
            <i className="fas fa-eye"></i>
          </button>
          <button
            className="btn btn-sm text-white btn-golden-yellow d-flex justify-content-center align-items-center"
            onClick={() => editService(row.serviceId)}
            title={t("edit_service")}
          >
            <i className="fas fa-edit"></i>
          </button>
          <button
            className="btn btn-sm text-white btn-red d-flex justify-content-center align-items-center"
            onClick={() => requestDeleteService(row.serviceId)}
            title={t("delete_service")}
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      ),

      style: { justifyContent: "flex-start" },
      ignoreRowClick: true,
      allowOverflow: true,
      width: "130px",
    },
  ];

  const customStyles = {
    table: {
      style: {
        minWidth: "900px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#f4f4f4",
        color: "#333",
        borderRight: "1px solid #ccc",
        fontSize: "16px",
        fontWeight: 600,
        whiteSpace: "nowrap",
        justifyContent: "flex-start",
      },
    },
    cells: {
      style: {
        borderRight: "1px solid #eee",
        fontSize: "13px",
        whiteSpace: "normal",
        wordBreak: "break-word",
        maxWidth: "300px",
        justifyContent: "flex-start",
      },
    },
    rows: {
      style: {
        minHeight: "50px",
      },
    },
  };

  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editServiceData, setEditServiceData] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [serviceMediaPreview, setServiceMediaPreview] = useState(null);
  const [serviceMediaError, setServiceMediaError] = useState("");
  const tenantId = localStorage.getItem("tenantId");
  const tenantUserId = localStorage.getItem("tenantUserId");

  useEffect(() => {
    getAllServices();
  }, []);

  const getAllServices = async () => {
    showLoader();
    try {
      const response = await apiManager.get(API_ENDPOINTS.DEALER_GET_SERVICES);
      if (response.status == 200) {
        const data = response.data;
        setServices(data);
      } else {
        showErrorMessage(t("failed_fetch_services"));
      }
    } catch (e) {
      showErrorMessage(e.message);
    } finally {
      hideLoader();
    }
  };

  const fetchServiceDetails = async (serviceId) => {
    try {
      const response = await apiManager.post(
        API_ENDPOINTS.DEALER_GET_SERVICE_BY_ID,
        { serviceId },
      );
      if (response.status == 200) {
        const data = response.data;
        const normalized = {
          ...data,
          isOptional:
            (data?.isOptional ??
            data?.IsOptional ??
            data?.optional ??
            data?.is_optional ??
            false)
              ? true
              : false,
        };
        setSelectedService(normalized);
        setShowModal(true);
      } else {
        showErrorMessage(t("failed_fetch_service_details"));
      }
    } catch (e) {
      showErrorMessage(e.message);
    }
  };

  const requestDeleteService = (id) => {
    setServiceToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDeleteService = async () => {
    try {
      showLoader();
      const response = await apiManager.post(
        API_ENDPOINTS.DEALER_DELETE_SERVICES,
        { serviceId: serviceToDelete },
      );
      if (response.status == 200) {
        showSuccessMessage(t("service_deleted"));
        await getAllServices();
        setShowDeleteModal(false);
        setServiceToDelete(null);
      } else {
        showErrorMessage(t("failed_delete_service"));
      }
    } catch (err) {
      showErrorMessage(err.message);
    } finally {
      hideLoader();
    }
  };

  const editService = async (id) => {
    try {
      showLoader();
      const response = await apiManager.post(
        API_ENDPOINTS.DEALER_GET_SERVICE_BY_ID,
        { serviceId: id },
      );
      if (response.status == 200) {
        const data = response.data;
        const normalizedIsOptional =
          (data?.isOptional ??
          data?.IsOptional ??
          data?.optional ??
          data?.is_optional ??
          false)
            ? true
            : false;
        setEditServiceData({
          serviceId: id,
          opCode: data.opCode || data.opcode || "",
          serviceName: data.serviceName || "",
          price: data.price || "",
          serviceType: data.serviceType || "",
          mileageInterval: data.mileageInterval || "",
          mileageIntervalUnit: data.mileageIntervalUnit || "miles",
          isMileageBased: data.isMileageBased || false,
          isOptional: normalizedIsOptional,
          ServiceImagePath: data.serviceImagePath || null,
          tenantId: tenantId,
          tenantUserId: tenantUserId,
        });
        setServiceMediaPreview(data.serviceImagePath || null);
        setServiceMediaError("");
        setShowFormModal(true);
      } else {
        showErrorMessage(t("failed_edit_service"));
      }
    } catch (err) {
      showErrorMessage(
        t("error_loading_service_for_edit") + ": " + err.message,
      );
    } finally {
      hideLoader();
    }
  };

  return (
    <section className="container-fluid content-wrapper select-vehicle-content">
      <div className="content form-card">
        <div className="page-title">
          <div className="container-fluid p-0">
            <div className="row">
              <div className="col-md-6 d-flex align-items-center">
                <h1>{t("shop_services")}</h1>
              </div>

              <div className="col-md-6 d-flex align-items-center justify-content-end">
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="d-flex justify-content-end"
                    style={{
                      position: "relative",
                      width: "250px",
                      marginLeft: "auto",
                    }}
                  >
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("search")}
                      style={{ width: "100%", paddingRight: "2.5rem" }}
                      value={gridSearch}
                      onChange={(e) => setGridSearch(e.target.value)}
                      id="shop-service-grid-search"
                    />
                    {gridSearch && (
                      <button
                        type="button"
                        className="btn btn-link p-0 position-absolute"
                        style={{
                          right: "8px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#888",
                          fontSize: "1.2rem",
                          textDecoration: "none",
                        }}
                        onClick={() => {
                          setGridSearch("");
                          setTimeout(() => {
                            document
                              .getElementById("shop-service-grid-search")
                              ?.focus();
                          }, 0);
                        }}
                        tabIndex={-1}
                        aria-label="Clear search"
                      >
                        <i className="fas fa-times-circle"></i>
                      </button>
                    )}
                  </div>

                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => {
                      setEditServiceData(null);
                      setServiceMediaPreview(null);
                      setServiceMediaError("");
                      setShowFormModal(true);
                    }}
                  >
                    {t("add_service")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row justify-content-center mt-3">
          <div className="col-lg-12">
            <div className="">
              <div className="card-body p-0">
                <DataTable
                  columns={serviceColumns}
                  data={services.filter((s) => {
                    const keyword = gridSearch.toLowerCase();
                    return (
                      s.serviceName?.toLowerCase().includes(keyword) ||
                      s.serviceType?.toLowerCase().includes(keyword) ||
                      (s.opCode || s.opcode || "").toLowerCase().includes(keyword) ||
                      (s.price !== undefined &&
                        s.price.toString().includes(keyword))
                    );
                  })}
                  pagination={services.length > 0}
                  striped
                  highlightOnHover
                  pointerOnHover
                  noDataComponent={t("no_record_found")}
                  responsive
                  customStyles={customStyles}
                  noHeader
                />
                <div className="mb-3">
                  <button
                    className="btn btn-blue btn-sm"
                    onClick={() => navigate("/admin-dashboard")}
                  >
                    {t("back")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {selectedService && (
        <div
          className={`modal fade ${showModal ? "show d-block" : ""}`}
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">{t("service_details")}</h5>
              </div>
              <div className="modal-body">
                <table className="table table-bordered mb-0">
                  <tbody>
                    {selectedService.serviceName && (
                      // <tr>
                      //   <th style={{ width: "40%" }}>Name</th>
                      //   <td>{selectedService.serviceName}</td>
                      // </tr>
                      <tr>
                        <th style={{ width: "40%" }}>{t("name")}</th>

                        <td
                          style={{
                            maxWidth: "300px", // optional: keeps table neat
                            wordWrap: "break-word", // break long words
                            whiteSpace: "normal", // allow multiple lines
                          }}
                        >
                          {selectedService.serviceName}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <th>OpCode</th>
                      <td>
                        {selectedService.opCode ||
                          selectedService.opcode ||
                          "N/A"}
                      </td>
                    </tr>
                    {selectedService.serviceType && (
                      <tr>
                        <th>{t("type")}</th>

                        <td>{selectedService.serviceType}</td>
                      </tr>
                    )}
                    {selectedService.price !== undefined && (
                      <tr>
                        <th>{t("price")}</th>

                        <td>
                          {(() => {
                            const code = (
                              selectedService.currency ||
                              selectedService.currencyCode ||
                              "USD"
                            ).toUpperCase();
                            const symbol = getCurrencySymbol(code);
                            const amount = Number(selectedService.price ?? 0);
                            return `${symbol}${amount.toFixed(2)}`;
                          })()}
                        </td>
                      </tr>
                    )}
                    {(selectedService.serviceImagePath ||
                      selectedService.ServiceImagePath) && (
                      <tr>
                        <th>Service Image / Video</th>
                        <td>
                          {/\.(mp4|mov|avi|webm|mkv)$/i.test(
                            String(
                              selectedService.serviceImagePath ||
                                selectedService.ServiceImagePath,
                            ),
                          ) ? (
                            <video
                              src={
                                selectedService.serviceImagePath ||
                                selectedService.ServiceImagePath
                              }
                              controls
                              style={{ maxWidth: "100%", maxHeight: "200px" }}
                              className="rounded"
                            />
                          ) : (
                            <img
                              src={
                                selectedService.serviceImagePath ||
                                selectedService.ServiceImagePath
                              }
                              alt="Service media"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "200px",
                                objectFit: "contain",
                              }}
                              className="img-thumbnail"
                            />
                          )}
                        </td>
                      </tr>
                    )}
                    {selectedService.isMileageBased !== undefined && (
                      <tr>
                        <th>{t("is_mileage_based")}</th>
                        <td>
                          {selectedService.isMileageBased ? t("yes") : t("no")}
                        </td>
                      </tr>
                    )}
                    {selectedService.isOptional !== undefined && (
                      <tr>
                        <th>{t("is_optional")}</th>
                        <td>
                          {selectedService.isOptional ? t("yes") : t("no")}
                        </td>
                      </tr>
                    )}
                    {selectedService.isMileageBased &&
                      !!selectedService.mileageInterval &&
                      Number(selectedService.mileageInterval) > 0 && (
                        <tr>
                          <th>{t("mileage_interval")}</th>

                          <td>{selectedService.mileageInterval}</td>
                        </tr>
                      )}
                    {selectedService.isMileageBased &&
                      selectedService.mileageIntervalUnit && (
                        <tr>
                          <th>{t("mileage_interval_unit")}</th>
                          <td>
                            {selectedService.mileageIntervalUnit
                              .charAt(0)
                              .toUpperCase() +
                              selectedService.mileageIntervalUnit.slice(1)}
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-danger"
                  onClick={() => setShowModal(false)}
                >
                  {t("close")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">{t("confirm_delete")}</h5>
              </div>
              <div className="modal-body">
                {services &&
                  serviceToDelete &&
                  (() => {
                    const svc = services.find(
                      (s) => s.serviceId === serviceToDelete,
                    );
                    return svc ? (
                      <div className="mb-2">
                        {t("confirm_delete_service")}:{" "}
                        <strong>{svc.serviceName}</strong>?
                      </div>
                    ) : (
                      <div className="mb-2">{t("confirm_delete_service")}</div>
                    );
                  })()}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-danger"
                  onClick={confirmDeleteService}
                >
                  {t("delete")}
                </button>
                <button
                  className="btn btn-cancel"
                  onClick={() => setShowDeleteModal(false)}
                >
                  {t("cancel")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showFormModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">
                  {editServiceData ? t("edit_service") : t("add_new_service")}
                </h5>
              </div>
              <div className="modal-body">
                <Formik
                  initialValues={
                    editServiceData || {
                      opCode: "",
                      serviceName: "",
                      price: "",
                      serviceType: null,
                      mileageInterval: "",
                      mileageIntervalUnit: "miles",
                      isMileageBased: false,
                      isOptional: false,
                      ServiceImagePath: null,
                      tenantId: tenantId,
                      tenantUserId: tenantUserId,
                    }
                  }
                  validationSchema={dealerServiceValidationSchema}
                  enableReinitialize
                  onSubmit={async (
                    values,
                    { setSubmitting, setStatus, resetForm },
                  ) => {
                    try {
                      showLoader();
                      const isEdit = !!editServiceData;
                      const opCodeVal = String(values.opCode ?? "").trim();
                      const submitValues = {
                        ...values,
                        opCode: opCodeVal,
                        price:
                          values.price !== "" ? Number(values.price) : null,
                        mileageInterval: isEdit
                          ? values.mileageInterval !== ""
                            ? Number(values.mileageInterval)
                            : null
                          : values.isMileageBased &&
                              values.mileageInterval?.toString().trim() !== ""
                            ? Number(values.mileageInterval)
                            : null,
                      };
                      const payload = {};
                      const formData = new FormData();
                      let fileKey = null;
                      let fileValue = null;

                      Object.entries(submitValues).forEach(([key, value]) => {
                        if (value instanceof File) {
                          fileKey = key;
                          fileValue = value;
                          return;
                        }
                        payload[key] = value;
                      });

                      if (!payload.ServiceImagePath) {
                        payload.ServiceImagePath = null;
                      }
                      const encryptedPayload = await encryptPayload(
                        JSON.stringify(payload),
                      );
                      formData.append("encryptedPayload", encryptedPayload);

                      if (fileKey && fileValue) {
                        formData.append(fileKey, fileValue);
                      }

                      const endpoint = isEdit
                        ? API_ENDPOINTS.DEALER_UPDATE_SERVICES
                        : API_ENDPOINTS.DEALER_CREATE_SERVICE;
                      const response = await apiManager.post(
                        endpoint,
                        formData
                      );

                      if (response.status == 200) {
                        showSuccessMessage(
                          isEdit ? t("service_updated") : t("service_created"),
                        );
                        await getAllServices();
                        if (!isEdit) resetForm();
                        setServiceMediaPreview(null);
                        setServiceMediaError("");
                        setShowFormModal(false);
                        setEditServiceData(null);
                      } else {
                        showErrorMessage(
                          isEdit
                            ? t("failed_update_service")
                            : t("failed_create_service"),
                        );
                      }
                    } catch (error) {
                      showErrorMessage(error);
                    } finally {
                      hideLoader();
                    }
                  }}
                >
                  {({
                    errors,
                    touched,
                    isSubmitting,
                    status,
                    values,
                    dirty,
                    setFieldValue,
                    setFieldTouched,
                  }) => (
                    <Form>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <FormLabel
                            htmlFor="serviceName"
                            label={t("service_name")}
                            required
                          />
                          <Field
                            name="serviceName"
                            type="text"
                            placeholder={t("enter_service_name")}
                            className={`form-control ${
                              touched.serviceName && errors.serviceName
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <div className="invalid-feedback">
                            {errors.serviceName}
                          </div>
                        </div>

                        <div className="col-md-6 mb-3">
                          <FormLabel
                            htmlFor="price"
                            label={t("price")}
                            required
                          />

                          <Field
                            name="price"
                            type="text"
                            placeholder={t("enter_price")}
                            onKeyDown={(e) => {
                              if (["e", "E", "+", "-"].includes(e.key))
                                e.preventDefault();
                            }}
                            onInput={(e) => {
                              const input = e.target;
                              let cursorPos = input.selectionStart;

                              let newValue = input.value
                                .replace(/[^0-9.]/g, "")
                                .replace(/(\..*?)\..*/g, "$1")
                                .replace(/^0+(\d)/, "$1")
                                .replace(/^(\d+\.\d{0,2}).*$/, "$1");

                              const diff = newValue.length - input.value.length;
                              cursorPos += diff > 0 ? diff : 0;

                              input.value = newValue;
                              input.setSelectionRange(cursorPos, cursorPos);
                            }}
                            className={`form-control ${
                              touched.price && errors.price ? "is-invalid" : ""
                            }`}
                          />
                          <div className="invalid-feedback">{errors.price}</div>
                        </div>

                        <div className="col-md-12 mb-3">
                          <FormLabel htmlFor="opCode" label="OpCode" />
                          <Field
                            name="opCode"
                            type="text"
                            placeholder="Enter OpCode"
                            onInput={(e) => {
                              e.target.value = e.target.value.replace(
                                /[^A-Za-z0-9]/g,
                                "",
                              );
                            }}
                            className={`form-control ${
                              touched.opCode && errors.opCode
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <div className="invalid-feedback">
                            {errors.opCode}
                          </div>
                        </div>

                        <div className="col-md-12 mb-3">
                          <FormLabel
                            htmlFor="ServiceImagePath"
                            label="Service Image / Video"
                          />
                          <div className="input-group d-flex gap-2 edit-upload-field">
                            <input
                              type="text"
                              className={`form-control ${
                                touched.ServiceImagePath &&
                                errors.ServiceImagePath
                                  ? "is-invalid"
                                  : ""
                              }`}
                              value={
                                values.ServiceImagePath instanceof File
                                  ? values.ServiceImagePath.name
                                  : typeof values.ServiceImagePath ===
                                        "string" && values.ServiceImagePath
                                    ? values.ServiceImagePath.split(
                                        /[\\/]/,
                                      ).pop()
                                    : "No file chosen"
                              }
                              readOnly
                            />
                            <input
                              id="ServiceImagePath"
                              name="ServiceImagePath"
                              type="file"
                              accept="image/png,image/jpeg,image/jpg,video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo,video/x-matroska"
                              className="d-none"
                              onChange={(e) => {
                                setFieldTouched(
                                  "ServiceImagePath",
                                  true,
                                  false,
                                );
                                const selected =
                                  e.currentTarget.files &&
                                  e.currentTarget.files[0]
                                    ? e.currentTarget.files[0]
                                    : null;

                                if (selected) {
                                  if (
                                    !SUPPORTED_FORMATS.includes(selected.type)
                                  ) {
                                    setServiceMediaError(
                                      t("validation_logo_file_format"),
                                    );
                                    e.target.value = "";
                                    return;
                                  }

                                  const maxAllowedSize =
                                    selected.type.startsWith("video/")
                                      ? VIDEO_FILE_SIZE
                                      : IMAGE_FILE_SIZE;

                                  if (selected.size > maxAllowedSize) {
                                    setServiceMediaError(
                                      t("validation_logo_file_size"),
                                    );
                                    e.target.value = "";
                                    return;
                                  }
                                  setServiceMediaError("");
                                  setFieldValue("ServiceImagePath", selected);
                                  setServiceMediaPreview(
                                    URL.createObjectURL(selected),
                                  );
                                } else {
                                  setServiceMediaError("");
                                  setFieldValue(
                                    "ServiceImagePath",
                                    values.ServiceImagePath || null,
                                  );
                                  setServiceMediaPreview(
                                    typeof values.ServiceImagePath === "string"
                                      ? values.ServiceImagePath
                                      : null,
                                  );
                                }
                              }}
                            />
                            <label
                              className={`btn btn-blue btn-outline-secondary ${
                                touched.ServiceImagePath &&
                                errors.ServiceImagePath
                                  ? "border-danger"
                                  : ""
                              }`}
                              htmlFor="ServiceImagePath"
                            >
                              <i className="fas fa-upload me-1"></i>{" "}
                              {t("browse")}
                            </label>
                          </div>
                          {touched.ServiceImagePath &&
                            errors.ServiceImagePath && (
                              <div className="invalid-feedback d-block">
                                {errors.ServiceImagePath}
                              </div>
                            )}
                          {serviceMediaError && (
                            <div className="text-danger small mt-2">
                              <i className="fas fa-exclamation-circle me-1"></i>
                              {serviceMediaError}
                            </div>
                          )}
                          {serviceMediaPreview && (
                            <div
                              className="mt-2 border rounded p-2 text-center"
                              style={{ backgroundColor: "#f8f9fa" }}
                            >
                              <p className="small text-muted mb-1">Preview:</p>
                              {(values.ServiceImagePath instanceof File
                                ? values.ServiceImagePath.type
                                : ""
                              ).startsWith("video/") ||
                              /\.(mp4|mov|avi|webm|mkv|ogg)$/i.test(
                                String(serviceMediaPreview),
                              ) ? (
                                <video
                                  src={serviceMediaPreview}
                                  controls
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: "200px",
                                  }}
                                  className="rounded"
                                />
                              ) : (
                                <img
                                  src={serviceMediaPreview}
                                  alt="Service media preview"
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: "200px",
                                    objectFit: "contain",
                                  }}
                                  className="img-thumbnail"
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mb-3">
                        <FormLabel
                          htmlFor="serviceType"
                          label={t("service_type")}
                          required
                        />
                        <Field name="serviceType">
                          {({ field, form, meta }) => {
                            const options = [
                              { value: "Maintenance", label: t("maintenance") },
                              { value: "Repair", label: t("repair") },
                              { value: "Inspection", label: t("inspection") },
                            ];
                            const isError = meta.touched && meta.error;
                            return (
                              <div>
                                <div className="dropdown-container">
                                  <Select
                                    id="serviceType"
                                    name={field.name}
                                    value={
                                      options.find(
                                        (option) =>
                                          option.value === field.value,
                                      ) || null
                                    }
                                    onChange={(option) => {
                                      form.setFieldValue(
                                        field.name,
                                        option?.value || "",
                                      );
                                      form.setFieldTouched(
                                        field.name,
                                        true,
                                        false,
                                      );
                                    }}
                                    onBlur={() =>
                                      form.setFieldTouched(field.name, true)
                                    }
                                    options={options}
                                    placeholder={t("select_type")}
                                    className={isError ? "is-invalid" : ""}
                                    classNamePrefix="select"
                                  />
                                </div>
                                {isError && (
                                  <div className="invalid-feedback d-block">
                                    {meta.error}
                                  </div>
                                )}
                              </div>
                            );
                          }}
                        </Field>
                      </div>
                      <div className="mb-3 d-flex align-items-center gap-4">
                        <div className="form-check form-check-inline mb-0">
                          <Field name="isMileageBased">
                            {({ field, form }) => (
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="isMileageBased"
                                checked={field.value}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  form.setFieldValue("isMileageBased", checked);
                                  if (!checked) {
                                    form.setFieldValue("mileageInterval", "");
                                    form.setFieldValue(
                                      "mileageIntervalUnit",
                                      "miles",
                                    );
                                  }
                                }}
                              />
                            )}
                          </Field>
                          <label
                            className="form-check-label"
                            htmlFor="isMileageBased"
                          >
                            {t("is_mileage_based")}
                          </label>
                        </div>
                        <div className="form-check form-check-inline mb-0">
                          <Field name="isOptional">
                            {({ field, form }) => (
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="isOptional"
                                checked={field.value}
                                onChange={(e) =>
                                  form.setFieldValue(
                                    "isOptional",
                                    e.target.checked,
                                  )
                                }
                              />
                            )}
                          </Field>
                          <label
                            className="form-check-label"
                            htmlFor="isOptional"
                          >
                            {t("is_optional")}
                          </label>
                        </div>
                      </div>

                      {values.isMileageBased && (
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <FormLabel
                              htmlFor="mileageInterval"
                              label={t("mileage_interval")}
                              required
                            />

                            <Field
                              name="mileageInterval"
                              type="text"
                              onKeyDown={(e) => {
                                if (["e", "E", "+", "-"].includes(e.key))
                                  e.preventDefault();
                              }}
                              onInput={(e) => {
                                const input = e.target;
                                let cursorPos = input.selectionStart;

                                let newValue = input.value
                                  .replace(/[^0-9.]/g, "")
                                  .replace(/(\..*?)\..*/g, "$1")
                                  .replace(/^0+(\d)/, "$1")
                                  .replace(/^(\d+\.\d{0,2}).*$/, "$1");

                                // adjust cursor if a dot was inserted or removed
                                const diff =
                                  newValue.length - input.value.length;
                                cursorPos += diff > 0 ? diff : 0;

                                input.value = newValue;
                                input.setSelectionRange(cursorPos, cursorPos);
                              }}
                              className={`form-control ${
                                touched.mileageInterval &&
                                errors.mileageInterval
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <div className="invalid-feedback">
                              {errors.mileageInterval}
                            </div>
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="">{t("interval_unit")}</label>
                            <Field name="mileageIntervalUnit">
                              {({ field, form }) => {
                                const options = [
                                  { value: "miles", label: t("miles") },
                                  {
                                    value: "kilometers",
                                    label: t("kilometers"),
                                  },
                                ];
                                return (
                                  <div className="dropdown-container">
                                    <Select
                                      id="mileageIntervalUnit"
                                      name={field.name}
                                      value={
                                        options.find(
                                          (option) =>
                                            option.value === field.value,
                                        ) || ""
                                      }
                                      onChange={(option) =>
                                        form.setFieldValue(
                                          field.name,
                                          option?.value || "",
                                        )
                                      }
                                      onBlur={field.onBlur}
                                      options={options}
                                      className={`${touched.mileageIntervalUnit && errors.mileageIntervalUnit ? "is-invalid" : ""}`}
                                      classNamePrefix="select"
                                      styles={{
                                        control: (base) => ({
                                          ...base,
                                          borderColor:
                                            touched.mileageIntervalUnit &&
                                            errors.mileageIntervalUnit
                                              ? "#dc3545"
                                              : "#ced4da",
                                          "&:hover": {
                                            borderColor:
                                              touched.mileageIntervalUnit &&
                                              errors.mileageIntervalUnit
                                                ? "#dc3545"
                                                : "#80bdff",
                                          },
                                          height: "calc(1.5em + 0.75rem + 2px)",
                                        }),
                                      }}
                                    />
                                  </div>
                                );
                              }}
                            </Field>
                            <div className="invalid-feedback">
                              {errors.mileageIntervalUnit}
                            </div>
                          </div>
                        </div>
                      )}
                      {status?.error && (
                        <div className="alert alert-danger">{status.error}</div>
                      )}
                      <div className="modal-footer pb-0 pe-0">
                        <button
                          type="submit"
                          className="btn btn-success"
                          disabled={
                            isSubmitting || (editServiceData ? !dirty : false)
                          }
                        >
                          {isSubmitting ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              ></span>{" "}
                              {editServiceData ? t("update") : t("saving")}
                            </>
                          ) : editServiceData ? (
                            t("update")
                          ) : (
                            t("save")
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-cancel"
                          onClick={() => {
                            setShowFormModal(false);
                            setEditServiceData(null);
                            setServiceMediaPreview(null);
                            setServiceMediaError("");
                          }}
                        >
                          {t("cancel")}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default DealerServices;
